import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAppStore } from '@/stores/appStore';
import { LoadingScreen } from '@/components/LoadingScreen';
import { NORTH_VALE_CENTER } from './core/config';
import { SceneSystem } from './systems/SceneSystem';
import { ModelSystem } from './systems/ModelSystem';
import { VehicleSystem } from './systems/VehicleSystem';
import { EffectsSystem } from './systems/EffectsSystem';
import { CameraSystem } from './systems/CameraSystem';
import { WeatherSystem } from './systems/WeatherSystem';
import { PanelSystem } from './systems/PanelSystem';
import { RouteSystem } from './systems/RouteSystem';
import { CitySystem } from './systems/CitySystem';
import { ScenarioRunner } from './scenario/ScenarioRunner';
import { getScenarioById } from './scenario/definitions';
import { addNetworkLayers } from './layers/networks';
import { addMarkerLayers } from './layers/markers';
import { addZoneBoundaries } from './layers/zones';
import { vehicleConfigs } from './data/vehicles';
import { NETWORK_LAYERS, AUTHORITY_LAYERS, AMBIENT_TRAFFIC_MODEL_IDS } from './core/constants';

export function MapView() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const cityRef = useRef<CitySystem | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [progress, setProgress] = useState(0);

  const xrayMode = useAppStore((s) => s.worldState.xrayMode);
  const authorityOverlay = useAppStore((s) => s.worldState.authorityOverlay);
  const renderStyle = useAppStore((s) => s.worldState.renderStyle);
  const setPanel = useAppStore((s) => s.setPanel);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

    // Final framed view the intro fly-in resolves to.
    const FINAL_VIEW = { center: NORTH_VALE_CENTER, zoom: 14.5, pitch: 65, bearing: -17 };
    // Establishing shot: zoomed out, top-down-ish, rotated — the reveal sweeps
    // down and in from here once everything has loaded.
    const INTRO_VIEW = { center: NORTH_VALE_CENTER, zoom: 12.8, pitch: 28, bearing: 25 };
    const MAX_BOUNDS: mapboxgl.LngLatBoundsLike = [
      [NORTH_VALE_CENTER[0] - 0.05, NORTH_VALE_CENTER[1] - 0.04],
      [NORTH_VALE_CENTER[0] + 0.05, NORTH_VALE_CENTER[1] + 0.04],
    ];

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: INTRO_VIEW.center,
      zoom: INTRO_VIEW.zoom,
      pitch: INTRO_VIEW.pitch,
      bearing: INTRO_VIEW.bearing,
      antialias: true,
      // maxBounds applied after the intro so the wider establishing shot
      // isn't clamped during the reveal.
    });

    map.on('load', async () => {
      darkenBaseMap(map);

      // 2D overlays (below Three.js)
      addNetworkLayers(map);
      addMarkerLayers(map, setPanel);
      addZoneBoundaries(map);

      // Systems
      const scene = new SceneSystem(NORTH_VALE_CENTER);
      map.addLayer(scene);

      // Apply initial style (sets fog, background, lighting)
      const initialStyle = useAppStore.getState().worldState.renderStyle;
      scene.applyStyle(initialStyle);

      // Phase-weighted loading progress: city tiles dominate, vehicles trail.
      const CITY_WEIGHT = 0.8;
      const VEHICLE_WEIGHT = 0.2;

      // Procedural city grid (Kenney tiles + infrastructure)
      const city = new CitySystem(scene);
      await city.generate((loaded, total) => {
        setProgress(total ? (loaded / total) * CITY_WEIGHT * 100 : 0);
      });
      cityRef.current = city;

      // ModelSystem still needed for scenario interactions (emissive, click handling)
      const models = new ModelSystem(scene, setPanel);
      await models.loadAll([]);

      const vehicles = new VehicleSystem(scene);
      await vehicles.preload((loaded, total) => {
        setProgress((CITY_WEIGHT + (total ? (loaded / total) * VEHICLE_WEIGHT : 0)) * 100);
      });
      setProgress(100);
      // Wire up city grid routing and style switching for vehicles
      vehicles.setCityRouter((fx, fz, tx, tz) => city.findRoute(fx, fz, tx, tz));
      city.setVehicleSystem(vehicles);
      startAmbientVehicles(vehicles);
      // Extra ambient cars wandering the road grid (no fixed destination).
      startWanderingTraffic(vehicles, city);

      const effects = new EffectsSystem(scene);
      const camera = new CameraSystem(map, scene);
      const weather = new WeatherSystem(map, { start: () => effects.startRain(0.6), stop: () => effects.stopRain(), setStormClouds: (s) => effects.setStormClouds(s) });
      const panels = new PanelSystem(map, scene);
      const routes = new RouteSystem(map);

      const runner = new ScenarioRunner({ camera, vehicles, effects, weather, panels, routes, models, city });

      const unsubscribe = useAppStore.subscribe((state, prevState) => {
        const curr = state.scenarioState.activeScenario;
        const prev = (prevState as typeof state)?.scenarioState?.activeScenario;
        if (curr && curr !== prev) {
          const scenario = getScenarioById(curr);
          if (scenario) {
            runner.play(scenario, () => useAppStore.getState().stopScenario());
          }
        }
      });

      map.on('click', (e) => models.handleClick(e.originalEvent, map.getCanvas()));

      mapRef.current = map;
      setMapReady(true);

      // Cinematic reveal: sweep down/in from the establishing shot to the
      // framed view as the loader fades out, then lock interaction bounds.
      const INTRO_MS = 3500;
      map.flyTo({
        center: FINAL_VIEW.center,
        zoom: FINAL_VIEW.zoom,
        pitch: FINAL_VIEW.pitch,
        bearing: FINAL_VIEW.bearing,
        duration: INTRO_MS,
        curve: 1.4,
        essential: true,
      });
      window.setTimeout(() => map.setMaxBounds(MAX_BOUNDS), INTRO_MS);

      cleanupRef.current = () => {
        unsubscribe();
        runner.stop();
        effects.dispose();
        vehicles.dispose();
        models.dispose();
        city.dispose();
      };
    });

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'bottom-right');

    return () => {
      cleanupRef.current?.();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [setPanel]);

  const applyXray = useCallback((mode: boolean) => {
    const map = mapRef.current;
    if (!map) return;
    const visibility = mode ? 'visible' : 'none';
    for (const layer of NETWORK_LAYERS) {
      if (map.getLayer(layer)) map.setLayoutProperty(layer, 'visibility', visibility);
    }
  }, []);

  useEffect(() => { if (mapReady) applyXray(xrayMode); }, [xrayMode, mapReady, applyXray]);

  const applyAuthority = useCallback((mode: boolean) => {
    const map = mapRef.current;
    if (!map) return;
    const visibility = mode ? 'visible' : 'none';
    for (const layer of AUTHORITY_LAYERS) {
      if (map.getLayer(layer)) map.setLayoutProperty(layer, 'visibility', visibility);
    }
  }, []);

  useEffect(() => { if (mapReady) applyAuthority(authorityOverlay); }, [authorityOverlay, mapReady, applyAuthority]);

  useEffect(() => {
    if (!mapReady || !cityRef.current) return;
    cityRef.current.switchStyle(renderStyle);
  }, [renderStyle, mapReady]);

  return (
    <>
      <div ref={mapContainer} className="w-full h-full" />
      <AnimatePresence>{!mapReady && <LoadingScreen progress={progress} />}</AnimatePresence>
    </>
  );
}

function darkenBaseMap(map: mapboxgl.Map): void {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const layer of style.layers) {
    if (layer.type === 'background') {
      try { map.setPaintProperty(layer.id, 'background-color', '#000000'); } catch {}
    } else {
      try { map.setLayoutProperty(layer.id, 'visibility', 'none'); } catch {}
    }
  }
}

// Ambient road-wandering traffic tuning.
const AMBIENT_WANDER_COUNT = 8;
const WANDER_MIN_CELLS = 36;
const WANDER_EXTRA_CELLS = 24;          // actual length: MIN + [0, EXTRA)
const WANDER_MIN_SPEED_KMH = 28;
const WANDER_SPEED_RANGE_KMH = 22;      // actual speed: MIN + [0, RANGE)
const WANDER_MIN_DURATION_MS = 8000;    // floor so very short loops aren't instant

function durationForRoute(route: [number, number][], speedKmh: number): number {
  return (routeDistanceKm(route) / speedKmh) * 3600 * 1000;
}

function startAmbientVehicles(vehicles: VehicleSystem): void {
  for (const config of vehicleConfigs) {
    const route = vehicles.fetchRoute(config.origin, config.destination);
    const durationMs = durationForRoute(route, config.speedKmh);
    vehicles.spawn(config.id, config.modelId, route, durationMs, { pingPong: true, label: config.label });
  }
}

function startWanderingTraffic(vehicles: VehicleSystem, city: CitySystem): void {
  for (let i = 0; i < AMBIENT_WANDER_COUNT; i++) {
    const route = city.wanderRoute(WANDER_MIN_CELLS + Math.floor(Math.random() * WANDER_EXTRA_CELLS));
    if (!route || route.length < 2) continue;
    const speedKmh = WANDER_MIN_SPEED_KMH + Math.random() * WANDER_SPEED_RANGE_KMH;
    const durationMs = Math.max(WANDER_MIN_DURATION_MS, durationForRoute(route, speedKmh));
    vehicles.spawn(
      `ambient-wander-${i}`,
      AMBIENT_TRAFFIC_MODEL_IDS[i % AMBIENT_TRAFFIC_MODEL_IDS.length],
      route,
      durationMs,
      { pingPong: true },
    );
  }
}

function routeDistanceKm(route: [number, number][]): number {
  let total = 0;
  for (let i = 0; i < route.length - 1; i++) {
    const dx = route[i + 1][0] - route[i][0];
    const dz = route[i + 1][1] - route[i][1];
    total += Math.sqrt(dx * dx + dz * dz);
  }
  return total / 1000;
}
